<!--
  Tres zonas y UN solo scroll: cabecera + indicador de pasos fijos arriba,
  cuerpo scrolleable, pie fijo abajo. El `min-h-0` del cuerpo es obligatorio:
  sin él, el hijo flex se niega a encogerse, el scroll se escapa al contenedor
  del modal y el pie con los botones queda fuera de alcance en el teléfono.

  En ≥600px `max-h-[85vh]` reproduce el tope que antes ponía `mat-dialog-content`
  (65vh de cuerpo + cabecera + pie), así que el escritorio se comporta igual.
-->
<div class="flex h-full max-h-[100dvh] w-[492px] max-w-full flex-col sm:max-h-[85vh]">
  @if (loadingStart() || submitting()) {
    <mat-progress-bar class="shrink-0" mode="indeterminate"></mat-progress-bar>
  }

  <!-- Cabecera: icono por estado + título + subtítulo -->
  <header class="flex shrink-0 items-start gap-3 px-4 pb-4 pt-4 sm:px-5">
    <div
      class="flex h-9 w-9 flex-none items-center justify-center rounded-md border"
      [ngClass]="headerIconClass()"
    >
      <mat-icon class="icon-size-5" [svgIcon]="headerIcon()"></mat-icon>
    </div>

    <div class="min-w-0 flex-1">
      <!-- Sin `truncate`: en 412px el título puede ocupar tres renglones y se
           lee entero; recortarlo escondería de qué va el modal. -->
      <h2
        mat-dialog-title
        class="m-0 text-pretty p-0 text-lg font-semibold leading-6 before:hidden"
      >
        {{ headerTitle() | transloco }}
      </h2>
      @if (headerSubtitle(); as subtitle) {
        <p class="text-secondary m-0 mt-1 text-pretty text-md leading-5">
          {{ subtitle | transloco }}
        </p>
      }
    </div>

    <!-- Sin X en el paso 2: cerrar ahí descartaría los códigos en silencio.
         Eso NO cambia en teléfono: `disableClose` sigue en true. -->
    @if (!recoveryCodes()) {
      <button
        data-test="mfa-close-x"
        class="-mt-1 -mr-2 !min-h-11 !min-w-11 flex-none"
        mat-icon-button
        [disabled]="submitting()"
        (click)="cancel()"
      >
        <mat-icon
          class="icon-size-5 text-gray-400"
          [svgIcon]="'heroicons_outline:x-mark'"
        ></mat-icon>
      </button>
    }
  </header>

  <!-- Indicador de pasos (el error de arranque no está en ningún paso).
       El rótulo «Paso 1 de 2» lleva `flex-none whitespace-nowrap`: si puede
       encogerse, en 412px se parte en dos renglones. El nombre del paso se
       esconde en teléfono en vez de dejar que la fila envuelva. -->
  @if (!startError()) {
    <div
      class="flex shrink-0 items-center gap-2 px-4 pb-3 text-xs font-bold uppercase tracking-wider text-gray-500 sm:px-5"
    >
      <span class="flex-none whitespace-nowrap">
        {{
          'mfa_setup_step_of'
            | transloco: { current: currentStep(), total: 2 }
        }}
      </span>
      <i class="h-[3px] flex-1 rounded-sm bg-primary"></i>
      <i
        class="h-[3px] flex-1 rounded-sm"
        [ngClass]="currentStep() === 2 ? 'bg-primary' : 'bg-slate-200'"
      ></i>
      <span
        data-test="mfa-step-name"
        class="hidden flex-none whitespace-nowrap text-gray-400 sm:inline"
      >
        {{
          (currentStep() === 2 ? 'mfa_setup_step2' : 'mfa_setup_step1')
            | transloco
        }}
      </span>
    </div>
  }

  <!-- Cuerpo: el ÚNICO scroll del modal -->
  <div
    data-test="mfa-dialog-body"
    class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-5 pt-1 sm:px-5"
  >
    <!-- Error de arranque: sin campo de código, sólo la salida -->
    @if (startError()) {
      <div data-test="mfa-start-error" class="py-2">
        <fuse-alert
          [appearance]="'outline'"
          [showIcon]="true"
          [type]="'error'"
          [@shake]="true"
        >
          {{ 'mfa_setup_start_error' | transloco }}
        </fuse-alert>
      </div>
    }

    <!-- Paso 0: skeletons con la forma final (el modal no salta de alto).
         El cuadrado del QR se mide con `qrBoxSize()`, la MISMA fuente que el
         ancho del `<qrcode>`: así sigue coincidiendo con la caja real en los
         dos tamaños (158px en escritorio, 206px en teléfono). -->
    @if (!startError() && loadingStart()) {
      <div data-test="mfa-skeleton" class="flex flex-col gap-4">
        <div
          class="flex flex-col items-stretch gap-3 rounded-lg border border-gray-200 bg-slate-50 p-3.5 sm:flex-row sm:items-center sm:gap-4 dark:border-gray-700 dark:bg-gray-800"
        >
          <div
            data-test="mfa-qr-skeleton"
            class="flex-none animate-pulse self-center rounded-md bg-gray-200 sm:self-auto dark:bg-gray-700"
            [style.width.px]="qrBoxSize()"
            [style.height.px]="qrBoxSize()"
          ></div>
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <div
              class="h-3 w-3/5 animate-pulse rounded bg-gray-200 dark:bg-gray-700"
            ></div>
            <div
              class="h-2.5 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700"
            ></div>
            <div
              class="h-2.5 w-4/5 animate-pulse rounded bg-gray-200 dark:bg-gray-700"
            ></div>
          </div>
        </div>
        <div
          class="h-[38px] animate-pulse rounded bg-gray-200 dark:bg-gray-700"
        ></div>
        <div
          class="h-[60px] w-[340px] max-w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700"
        ></div>
      </div>
    }

    <!-- Paso 1: escanear el QR + confirmar el primer código -->
    @if (!startError() && !loadingStart() && otpauthUri() && !recoveryCodes()) {
      <app-mfa-enroll-step
        [control]="code"
        [otpauthUri]="otpauthUri()"
        [secret]="secret()"
        [errorKey]="codeError()"
        (confirmed)="confirm()"
      ></app-mfa-enroll-step>
    }

    <!-- Paso 2: MFA activado, mostrar los recovery codes (única vez) -->
    @if (recoveryCodes(); as codes) {
      <div class="flex flex-col gap-4">
        <fuse-alert
          [appearance]="'outline'"
          [showIcon]="true"
          [type]="'success'"
        >
          {{ 'mfa_setup_success' | transloco }}
        </fuse-alert>

        <app-mfa-recovery-codes
          [codes]="codes"
          (acknowledged)="acknowledged.set($event)"
        ></app-mfa-recovery-codes>
      </div>
    }
  </div>

  <!--
    Pie fijo, hermano del scroller: nunca sale de alcance del pulgar. En
    teléfono los botones ocupan todo el ancho (48px de alto); en ≥600px vuelven
    al tamaño y la alineación a la derecha de siempre.

    `!min-h-*` además de `!h-*` NO es redundante: Fuse le pone
    `max-height: 40px` a todo botón de Material, y un `height` mayor queda
    clampeado. `min-height` gana sobre `max-height` y es lo que realmente
    levanta el botón a 48px.
  -->
  <footer
    class="flex shrink-0 gap-2.5 border-t border-gray-200 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:justify-end sm:px-5 dark:border-gray-700"
  >
    @if (startError()) {
      <button
        class="!h-12 !min-h-12 flex-1 sm:!h-10 sm:!min-h-10 sm:!flex-none"
        mat-stroked-button
        (click)="cancel()"
      >
        {{ 'mfa_setup_close' | transloco }}
      </button>
      <button
        data-test="mfa-retry"
        class="!h-12 !min-h-12 flex-1 sm:!h-10 sm:!min-h-10 sm:!flex-none"
        mat-flat-button
        color="primary"
        (click)="retry()"
      >
        {{ 'mfa_setup_retry' | transloco }}
      </button>
    } @else if (!recoveryCodes()) {
      <button
        class="!h-12 !min-h-12 flex-1 sm:!h-10 sm:!min-h-10 sm:!flex-none"
        mat-stroked-button
        (click)="cancel()"
        [disabled]="submitting()"
      >
        {{ 'cancel' | transloco }}
      </button>
      @if (!loadingStart()) {
        <button
          data-test="mfa-activate"
          class="!h-12 !min-h-12 flex-1 sm:!h-10 sm:!min-h-10 sm:!flex-none"
          mat-flat-button
          color="primary"
          [disabled]="submitting() || !codeValid()"
          (click)="confirm()"
        >
          {{ 'mfa_setup_activate' | transloco }}
        </button>
      }
    } @else {
      <button
        data-test="mfa-done"
        class="!h-12 !min-h-12 flex-1 sm:!h-10 sm:!min-h-10 sm:!flex-none"
        mat-flat-button
        color="primary"
        [disabled]="!acknowledged()"
        (click)="done()"
      >
        {{ 'mfa_setup_recovery_done' | transloco }}
      </button>
    }
  </footer>
</div>
